import * as deepmerge from 'deepmerge';
import { setStorage, getStorage } from 'js/storage';

export default class NotificationBar {
    DEFAULT_CONFIG = {
        selectors: {
            notificationBar: '.notification-bar',
            closeButton: '.notification-bar__close',
            mobileAppBanner: '.app-promo-header',
            intro: '.notification-bar__intro',
            description: '.notification-bar__description'
        },
        classNames: {
            notificationBar: 'notification-bar',
            wrapper: 'notification-bar__wrapper',
            closeButton: 'notification-bar__close',
            button: 'notification-bar__button button',
            title: 'notification-bar__title',
            intro: 'notification-bar__intro',
            description: 'notification-bar__description'
        },
        storageKey: 'notification-bar',
        invalidHtml: /<(?!(\/?(em|strong))).+?>/
    };

    /**
     * Creates an instance of notificationBar.
     * @param {HTMLFormElement} node
     */
    constructor(config = {}) {
        this.config = deepmerge(this.config, this.DEFAULT_CONFIG, config);
        this.init();
    }

    init() {
        // Check in storage if there is a notification bar active:
        this.checkNotificationStorage();
    }

    /**
     * The main trigger that is binded to window.viabovag.showNotification(config)
     * @param {object} config
     */
    showNotification(config) {
        if (!config) {
            return;
        }

        /** Did the visitor discarded this notification before? */
        if (this.notificationStorage.includes(config.id)) {
            return;
        }

        this.removeNotification();

        if (config.intro && config.description) {
            const invalidIntro = this.config.invalidHtml.test(config.intro);
            const invalidDescription = this.config.invalidHtml.test(config.intro);

            if (!invalidIntro || !invalidDescription) {
                this.createNotificationBarElement(config);
            }
        }
    }

    /**
     * Remove the notification from the DOM,
     * this function can be used via: window.viabovag.removeNotification()
     */
    removeNotification() {
        if (this.node) {
            this.node.remove();
        }
    }

    /**
     * Creates the notification-bar component and prepends it to the body
     * @param {object} config
     */
    createNotificationBarElement(config) {
        const appBanner = document.querySelector(this.config.selectors.mobileAppBanner);
        if (appBanner && !appBanner.hidden) {
            // If the app-banner is not hidden, don't show the notification bar
            return;
        }

        const notificationBar = document.createElement('a');
        notificationBar.className = this.config.classNames.notificationBar;
        notificationBar.target = config.newWindow ? '_blank' : '_self';
        notificationBar.href = config.url;
        notificationBar.setAttribute('data-js-notification-bar', 'notification');

        notificationBar.innerHTML = `
            <div class="notification-bar__wrapper">
                <h2 class="notification-bar__title">${config.title}</h2>
                <span class="notification-bar__intro"></span>
                <span class="notification-bar__description"></span>
                <button class="button notification-bar__button">${config.buttonText}</button>
                <button class="notification-bar__close">x</button>
            </div>
        `;

        document.body.prepend(notificationBar);

        this.node = document.querySelector(this.config.selectors.notificationBar);
        const intro = this.node.querySelector(this.config.selectors.intro);
        const description = this.node.querySelector(this.config.selectors.description);
        const closeButton = this.node.querySelector(this.config.selectors.closeButton);

        // Update innerHTML for Intro and Description as it may have html tags: b,i and u.
        intro.innerHTML = config.intro;
        description.innerHTML = config.description;

        closeButton.addEventListener('click', e => this.handleCloseNotification(e, config.id));
    }

    /**
     * When the X is clicked on the notification
     */
    handleCloseNotification(e, id) {
        if (e.target.classList.contains(this.config.classNames.closeButton)) {
            // Don't navigate when the button pressed the close X icon
            e.preventDefault();
        }

        // Remove the node and update the localstorage
        this.removeNotification();
        this.saveNotificationHideSetting(id);
    }

    /**
     * When user want's to hide the message, remember it.
     */
    saveNotificationHideSetting(id) {
        this.notificationStorage.push(id);
        setStorage(this.config.storageKey, this.notificationStorage);
    }

    /**
     * Checks in storage if there are notifications that should not be visible anymore
     */
    checkNotificationStorage() {
        this.notificationStorage = getStorage(this.config.storageKey) || [];

        // Fallback for older storage objects:
        if (this.notificationStorage && !Array.isArray(this.notificationStorage)) {
            this.notificationStorage = this.notificationStorage.seen ? [this.notificationStorage.id] : [];
        }
    }
}
